import { useState, type FormEvent } from 'react'

import { AuthChrome, PasswordField } from '../../components/AuthChrome'
import { Button, Field, Notice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'

/**
 * Cognito's `CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED` challenge — the first
 * sign-in after an administrator creates an account.
 *
 * `pendingMissingAttributes` is why this screen is not just a password box.
 * Cognito refuses the challenge with "name is missing" unless the attributes it
 * flagged are sent alongside the new password, so whatever it asked for is
 * rendered as a field and forwarded.
 */
export function ForcedReset({ onDone }: { onDone: () => void }) {
  const { confirmNewPassword, pendingMissingAttributes, authError, signOut } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [attributes, setAttributes] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (password !== confirm) {
      setLocalError('Those two passwords do not match.')
      return
    }
    const missing = pendingMissingAttributes.filter((a) => !attributes[a]?.trim())
    if (missing.length) {
      setLocalError(`Cognito also needs: ${missing.join(', ')}.`)
      return
    }
    setLocalError(null)
    setIsSubmitting(true)
    try {
      await confirmNewPassword(password, attributes)
      onDone()
    } catch {
      // authError carries it.
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthChrome
      eyebrow="First sign-in"
      title="Choose a new password"
      lead="Your account was created with a temporary password. Set your own to continue."
      footer={
        <button type="button" className="auth__link auth__link--block" onClick={() => void signOut().then(onDone)}>
          Start again
        </button>
      }
    >
      <form className="auth__form" onSubmit={handleSubmit} noValidate>
        {(localError || authError) && <Notice tone="error">{localError ?? authError}</Notice>}

        {pendingMissingAttributes.map((attribute) => (
          <Field
            key={attribute}
            label={attribute === 'name' ? 'Your name' : attribute}
            value={attributes[attribute] ?? ''}
            autoComplete={attribute === 'name' ? 'name' : 'off'}
            onChange={(e) => setAttributes((a) => ({ ...a, [attribute]: e.target.value }))}
          />
        ))}

        <PasswordField
          label="New password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          autoFocus={pendingMissingAttributes.length === 0}
        />
        <PasswordField
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />

        <Button type="submit" full busy={isSubmitting}>
          {isSubmitting ? 'Saving' : 'Set password and continue'}
        </Button>
      </form>
    </AuthChrome>
  )
}
